import { type OverviewScreenProps } from "@finq/pension-portfolio/components/pension/portfolio/OverviewTypes/Shared"

import { PolicyStatusEnum } from "../types/portfolio"
import { PolicyStatuses } from "./pension-portfolio.constants"

export const usePensionPortfolioTextHelpers = () => {
  /**
   *
   * @param value
   * @param useValueAsExists - if true, returns the value as is, otherwise returns 'יש' or 'אין'
   * @returns
   */
  const toExists = (value: Nullable<boolean | string | number>, useValueAsExists?: boolean): string => {
    return value ? (useValueAsExists ? String(value) : "יש") : "אין"
  }

  const toActive = (value: Nullable<boolean | string | number>) => (value ? "פעיל" : "לא פעיל")

  /**
   * Either returns the value as 'ללא הפקדות', or formats it as cash with the given currency symbol
   */
  const toDeposits = (value: Nullable<number>, currency = NIS, toFixed?: number): string | number => {
    return value ? toCash(value, currency, toFixed) : "ללא הפקדות"
  }

  /**
   * Either returns the value as 'אין', or formats the date as 'DD.MM.YYYY'
   */
  const formatDate = (date: Nullable<string | number | Date>, dateFormat = "DD.MM.YYYY") => {
    const daysjs = useDayjs()
    // check if date is unix timestamp
    let dayjsDate = daysjs(date)
    if (typeof date === "number") dayjsDate = daysjs.unix(date)

    return date ? dayjsDate.format(dateFormat) : toExists(date, true)
  }

  /**
   * Either returns the value as 'אין', or formats it as cash with the given currency symbol
   */
  const toExistsWithCash = (value: Nullable<boolean | string | number>, currency = NIS, toFixed?: number) => {
    return value ? toCash(value, currency, toFixed) : toExists(value)
  }

  const toStatus = (value: PolicyStatusEnum) => {
    if (!Object.values(PolicyStatusEnum).includes(value)) return value

    return PolicyStatuses[value]
  }

  const extractFrom = <T extends any>(obj: T, value: Nullable<keyof T | (string & {})>): T[keyof T] => {
    return obj[value as keyof T]
  }

  const toYesNoUnknown = (value: Nullable<boolean>): string => {
    if (isNullOrUndefined(value)) return "לא ידוע"
    return value ? "כן" : "לא"
  }

  const toUnknown = <T extends number | string>(
    value: Nullable<T>,
    func?: (value: Nullable<T>, isUnknown?: boolean, fixed?: number) => string | number
  ): string | number => {
    if (isNullOrUndefined(value)) return "לא ידוע"
    if (value instanceof Number) return func ? func(value, true, 2) : (value as string | number)
    return func ? func(value, true) : (value as string | number)
  }

  return {
    toExists,
    toActive,
    toDeposits,
    toExistsWithCash,
    formatDate,
    toStatus,
    extractFrom,
    toYesNoUnknown,
    toUnknown,
  }
}

export const useCurrentProductSettings = (props: Omit<OverviewScreenProps, "class">) => {
  const category = computed(() => props.category)
  const productCategory = computed(() => {
    const catId = props.policy.productCategoryId

    if (catId !== category.value) return catId

    return null
  })

  const hasSubCategory = computed(() => Boolean(productCategory.value))

  return {
    category,
    productCategory,
    hasSubCategory,
  }
}
